import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/empty-state';
import { HealthScoreRing } from '@/components/shared/health-score-ring';
import { SeverityBadge } from '@/components/dashboard/severity-badge';
import { DashboardSummary, type DashboardSummaryData } from '@/components/dashboard/dashboard-summary';
import { FolderOpen, Plus, ExternalLink, ArrowRight } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface ProjectRow {
  projectId: string;
  name: string;
  targetUrl: string;
  healthScore: number;
  findingCounts: Record<string, number>;
  lastScanAt: string | null;
  status: string;
}

interface PortfolioRow {
  portfolioId: string;
  name: string;
  description: string | null;
  isDefault: boolean | null;
}

/**
 * Portfolio detail page — shows the portfolio-scoped dashboard widgets at
 * the top followed by the list of projects in this portfolio. Mirrors
 * the org dashboard but scoped down one level so users can see the
 * security posture of a specific group of projects in isolation.
 */
export function ProjectsPage() {
  const navigate = useNavigate();
  const { orgId, portfolioId } = useParams<{ orgId: string; portfolioId: string }>();
  const enabled = !!orgId && !!portfolioId;

  const { data: portfolioRes } = useQuery({
    queryKey: ['portfolio', orgId, portfolioId],
    queryFn: () => apiClient.getPortfolio(orgId!, portfolioId!),
    enabled,
    staleTime: 60_000,
  });
  const portfolio = portfolioRes?.data as PortfolioRow | undefined;

  const { data: dashRes } = useQuery({
    queryKey: ['portfolio', orgId, portfolioId, 'dashboard'],
    queryFn: () => apiClient.getPortfolioDashboardSummary(orgId!, portfolioId!),
    enabled,
  });
  const dash = (dashRes?.data as DashboardSummaryData | undefined) ?? null;

  const { data: projectsRes, isLoading } = useQuery({
    queryKey: ['org', orgId, 'portfolio', portfolioId, 'projects'],
    queryFn: () => apiClient.listProjects(orgId!, portfolioId!),
    enabled,
  });
  const projects = (projectsRes?.data as ProjectRow[] | undefined) ?? [];

  const newProjectHref = `/orgs/${orgId}/portfolios/${portfolioId}/projects/new`;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-muted rounded w-48 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-5 bg-gray-200 dark:bg-muted rounded w-40 mb-2" />
              <div className="h-4 bg-gray-100 dark:bg-muted rounded w-56" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2 flex-wrap">
            {portfolio?.name ?? 'Portfolio'}
            {portfolio?.isDefault && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                Default
              </span>
            )}
          </h1>
          {portfolio?.description ? (
            <p className="text-muted-foreground mt-1">{portfolio.description}</p>
          ) : (
            <p className="text-muted-foreground mt-1">
              {projects.length} project{projects.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <Link to={newProjectHref}>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </Link>
      </div>

      {/* Empty-portfolio shortcut: no widgets, just the CTA. */}
      {projects.length === 0 && (
        <EmptyState
          icon={FolderOpen}
          title="No projects in this portfolio yet"
          description="Add your first project to start scanning. Just paste a website URL or connect a repository — we'll take it from there."
          actionLabel="Add Your First Project"
          onAction={() => navigate(newProjectHref)}
        />
      )}

      {/* Dashboard widgets, with the project cards injected just before
       * "Recent Agent Runs" so navigation flows top-down: stats → chart
       * → projects → recent activity. */}
      {projects.length > 0 && dash && (
        <DashboardSummary
          orgId={orgId!}
          drillHref={`/orgs/${orgId}/portfolios/${portfolioId}`}
          runHref={(run) =>
            run.portfolioId
              ? `/orgs/${orgId}/portfolios/${run.portfolioId}/projects/${run.projectId}/agents/runs/${run.runId}`
              : null
          }
          data={dash}
          beforeRecentRuns={
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-foreground uppercase tracking-wide">Projects</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {projects.map((project) => {
                  const score = project.healthScore ?? null;
                  const accent = score === null
                    ? { gradient: 'from-gray-500/10 to-gray-600/5 dark:from-gray-500/15 dark:to-gray-600/10', border: 'border-gray-200/60 dark:border-border/40' }
                    : score >= 80
                    ? { gradient: 'from-green-500/10 to-emerald-600/5 dark:from-green-500/15 dark:to-emerald-600/10', border: 'border-green-200/60 dark:border-green-500/30' }
                    : score >= 50
                    ? { gradient: 'from-yellow-500/10 to-orange-500/5 dark:from-yellow-500/15 dark:to-orange-500/10', border: 'border-yellow-200/60 dark:border-yellow-500/30' }
                    : { gradient: 'from-red-500/10 to-orange-500/5 dark:from-red-500/15 dark:to-orange-500/10', border: 'border-red-200/60 dark:border-red-500/30' };

                  return (
                    <Link key={project.projectId} to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${project.projectId}`}>
                      <Card className={cn('!p-0 !gap-0 hover:shadow-xl dark:hover:shadow-gray-900/60 transition-all hover:-translate-y-1 cursor-pointer overflow-hidden bg-gradient-to-br', accent.gradient, accent.border)}>
                        <div className="flex flex-col sm:flex-row">
                          <div className="w-full sm:w-52 sm:shrink-0 flex flex-col items-center justify-center px-5 py-6 sm:py-8 bg-white/50 dark:bg-card/80 border-b sm:border-b-0 sm:border-r border-gray-200/50 dark:border-border/50">
                            <HealthScoreRing score={score} size={140} strokeWidth={6} />
                          </div>

                          <div className="flex-1 min-w-0 px-5 sm:px-8 py-5 sm:py-6 flex flex-col justify-between gap-4">
                            <div>
                              <h3 className="text-lg font-bold text-gray-900 dark:text-foreground truncate">{project.name}</h3>
                              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1.5 truncate">
                                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">{project.targetUrl}</span>
                              </p>

                              {project.findingCounts && (
                                <div className="flex flex-wrap gap-2 mt-4">
                                  {(['critical', 'high', 'medium', 'low'] as const)
                                    .map((sev) => ({ sev, count: (project.findingCounts[sev] ?? 0) as number }))
                                    .filter(({ count }) => count > 0)
                                    .map(({ sev, count }) => (
                                      <SeverityBadge key={sev} severity={sev} count={count} />
                                    ))}
                                  {(['critical', 'high', 'medium', 'low'] as const).every((s) => !project.findingCounts[s]) && (
                                    <span className="text-xs text-muted-foreground">No findings yet</span>
                                  )}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between gap-3 flex-wrap pt-4 border-t border-gray-200/50 dark:border-border/50">
                              <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                                {project.lastScanAt ? 'Last scanned recently' : 'Not scanned yet'}
                              </span>
                              <span className="text-sm font-semibold text-primary-strong flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
                                View details <ArrowRight className="h-3.5 w-3.5" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
